<script setup>
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { useMediaQuery } from '@composables/use-media-query'
import { useDismissable } from '@composables/use-dismissable'
import { MEDIA_TABLET_UP } from '@design/breakpoints'

const props = defineProps({
    items: {
        type: Array,
        default: () => [],
    },
    header: {
        type: Object,
        default: null,
    },
    // auto = popover from the tablet tier up, bottom sheet on phones.
    surface: {
        type: String,
        default: 'auto',
        validator: value => ['auto', 'popover', 'sheet'].includes(value),
    },
    // Which edge of the popover meets the trigger.
    align: {
        type: String,
        default: 'end',
        validator: value => ['start', 'end'].includes(value),
    },
    triggerLabel: {
        type: String,
        default: 'Options',
    },
    // The default kebab is the M circle; table rows use S so the 48px row holds (R17).
    triggerSize: {
        type: String,
        default: 'm',
        validator: value => ['m', 's'].includes(value),
    },
})

const emit = defineEmits(['open', 'close', 'select'])

// The popover keeps this far from the side edges of its horizontal bounds. Mirrors the
// phone/tablet page padding ($space-16 in _tokens.scss) — JS can't read the SCSS token, so
// keep them in step.
const VIEWPORT_GUTTER = 16

// useId() is stable across a server render and its hydration (Story 3 SSR-determinism
// rule) — a module-level counter would drift between requests and desync aria-controls.
const id = `context-menu-${useId()}`
const isOpen = ref(false)
const root = ref(null)
const surfaceEl = ref(null)
const panel = ref(null)
const scrim = ref(null)
const isTabletUp = useMediaQuery(MEDIA_TABLET_UP)

const isSheet = computed(() => props.surface === 'sheet' || (props.surface === 'auto' && !isTabletUp.value))

// Edge correction (popover only): set while open from a measurement, cleared on close so
// every open starts from the caller's `align` and re-measures.
const alignOverride = ref(null)
const nudge = ref(0)
const resolvedAlign = computed(() => alignOverride.value || props.align)

// The nudge moves the anchored edge's offset, never `transform` — the dialog enter
// transition animates transform on this same element.
const surfaceStyle = computed(() => {
    if (isSheet.value || !nudge.value) return null
    return resolvedAlign.value === 'start' ? { left: `${nudge.value}px` } : { right: `${-nudge.value}px` }
})

function visibleWidth(left, width, min, max) {
    return Math.max(0, Math.min(left + width, max) - Math.max(left, min))
}

// Browser only (called from correctPopoverEdges). The horizontal room the popover can be
// seen in: the viewport intersected with the nearest ancestor that clips horizontally — the
// app's `.main` column is `overflow-x: clip` and starts right of the desktop sidebar, so a
// panel that fits the viewport can still lose its left edge under it. Rect coordinates live
// in the layout viewport, which excludes a classic vertical scrollbar — innerWidth includes
// it, so it's only the fallback when there's no layout (clientWidth 0).
function horizontalBounds() {
    let left = 0
    let right = document.documentElement.clientWidth || window.innerWidth
    for (let el = root.value.parentElement; el && el !== document.documentElement; el = el.parentElement) {
        const overflowX = window.getComputedStyle(el).overflowX
        if (overflowX && overflowX !== 'visible') {
            const rect = el.getBoundingClientRect()
            left = Math.max(left, rect.left)
            right = Math.min(right, rect.right)
            break
        }
    }
    return { min: left + VIEWPORT_GUTTER, max: right - VIEWPORT_GUTTER }
}

// Browser only: called from open()'s nextTick, after the popover is in the DOM but before
// the browser paints it, so the corrected position lands in the first painted frame.
// Returns whether it changed the position.
function correctPopoverEdges() {
    if (typeof window === 'undefined' || !isOpen.value || isSheet.value) return false
    if (!root.value || !surfaceEl.value) return false
    // offsetWidth is the layout width: the rect would be shrunk by the enter scale(.98).
    const width = surfaceEl.value.offsetWidth
    if (!width) return false
    const trigger = root.value.getBoundingClientRect()
    const { min, max } = horizontalBounds()
    const leftFor = align => (align === 'start' ? trigger.left : trigger.right - width)
    const fits = align => leftFor(align) >= min && leftFor(align) + width <= max

    const preferred = props.align
    const other = preferred === 'start' ? 'end' : 'start'
    if (fits(preferred)) return false
    if (fits(other)) {
        alignOverride.value = other
        return true
    }
    const chosen = visibleWidth(leftFor(other), width, min, max) > visibleWidth(leftFor(preferred), width, min, max)
        ? other
        : preferred
    const left = leftFor(chosen)
    // Clamp into [min, max - width]; when the bounds are narrower than panel + gutters the
    // left edge wins.
    const target = Math.max(min, Math.min(left, max - width))
    alignOverride.value = chosen === preferred ? null : chosen
    nudge.value = target - left
    return true
}

// Hide, never disable; dividers collapse with their group and never lead or trail.
const rows = computed(() => {
    const out = []
    props.items.forEach(item => {
        if (item.divider) {
            if (out.length && !out[out.length - 1].divider) out.push({ divider: true })
            return
        }
        if (item.hidden) return
        out.push(item)
    })
    while (out.length && out[out.length - 1].divider) out.pop()
    return out
})

const hasMenu = computed(() => rows.value.some(row => !row.divider))

const triggerAttrs = computed(() => ({
    'aria-haspopup': 'menu',
    'aria-expanded': isOpen.value ? 'true' : 'false',
    'aria-controls': id,
}))

function triggerElement() {
    return root.value ? root.value.querySelector('[aria-haspopup="menu"]') : null
}

function open() {
    if (isOpen.value || !hasMenu.value) return
    isOpen.value = true
    emit('open')
    nextTick(async () => {
        // Focus after a correction is patched in, so focus() never scrolls the page toward
        // the clipped position.
        if (correctPopoverEdges()) await nextTick()
        const first = panel.value && panel.value.querySelector('[role="menuitem"]')
        first && first.focus()
    })
}

// Controller ruling (fix round 1): focus returns to the trigger ONLY on the Escape and
// row-activation paths (navigating rows included — the focus is `preventScroll`, and a
// trigger the new route unmounts simply takes the focus with it), never on
// outside-pointerdown / scrim / route-change / hasMenu going false, and never for the
// exposed `close()` — a dismissal the user didn't aim at the menu, or a programmatic
// close, must not steal focus or yank a scrolled-off popover back into view.
function close({ restoreFocus = false } = {}) {
    if (!isOpen.value) return
    isOpen.value = false
    alignOverride.value = null
    nudge.value = 0
    emit('close')
    if (!restoreFocus) return
    nextTick(() => {
        const trigger = triggerElement()
        trigger && trigger.focus({ preventScroll: true })
    })
}

function toggle() {
    isOpen.value ? close() : open()
}

function activate(item) {
    emit('select', item)
    if (typeof item.action === 'function') item.action()
    close({ restoreFocus: true })
}

// useDismissable's single onDismiss fires for Escape, outside-pointerdown and route
// change alike, and its interface stays untouched — so Escape is handled a second time
// here, in a capture-phase document listener, specifically to restore focus. A real key
// press targets the focused element (or body), so this listener runs on the way down,
// before useDismissable's bubble-phase document listener, whichever was added first. By
// the time useDismissable's own Escape handler calls `close()` afterwards, isOpen is
// already false and it's a no-op.
function onEscapeCapture(event) {
    if (event.key === 'Escape') close({ restoreFocus: true })
}

watch(isOpen, nowOpen => {
    if (typeof document === 'undefined') return
    if (nowOpen) {
        document.addEventListener('keydown', onEscapeCapture, true)
    } else {
        document.removeEventListener('keydown', onEscapeCapture, true)
    }
}, { immediate: true })

onBeforeUnmount(() => {
    if (typeof document === 'undefined') return
    document.removeEventListener('keydown', onEscapeCapture, true)
})

// D18: a downward touch swipe on the sheet panel closes it, as the grabber invites. Touch
// only (a mouse drag on the popover keeps selecting text) and sheet only. Same dismissal
// class as the scrim: no focus restore. One finger only: a second finger (a pinch) or a
// cancelled touch drops the gesture.
const SWIPE_CLOSE_PX = 48
let touchStartY = null

function onPanelTouchStart(event) {
    touchStartY = isSheet.value && event.touches.length === 1 ? event.touches[0].clientY : null
}

function onPanelTouchCancel() {
    touchStartY = null
}

function onPanelTouchEnd(event) {
    if (touchStartY === null) return
    if (event.touches.length > 0) {
        touchStartY = null
        return
    }
    const travelled = event.changedTouches[0].clientY - touchStartY
    touchStartY = null
    if (travelled >= SWIPE_CLOSE_PX) close()
}

// The scrim counts as inside: a pointerdown on it must not dismiss, or the sheet would fade
// out mid-tap and the finger's click would land on whatever sits beneath (a ghost click).
// The scrim's own @click closes the sheet once the tap completes.
useDismissable({ isOpen, onDismiss: () => close(), inside: () => [panel.value, triggerElement(), scrim.value] })

watch(hasMenu, has => {
    if (!has) close()
})

defineExpose({ open, close })
</script>

<template>
    <div
        v-if="hasMenu"
        ref="root"
        class="base-context-menu"
    >
        <slot
            name="trigger"
            :toggle="toggle"
            :is-open="isOpen"
            :attrs="triggerAttrs"
        >
            <BaseButton
                v-bind="triggerAttrs"
                variant="tertiary"
                :size="triggerSize"
                icon-only
                icon="more"
                :label="triggerLabel"
                :selected="isOpen"
                @click="toggle"
            />
        </slot>

        <Teleport
            to="body"
            :disabled="!isSheet"
        >
            <transition :name="isSheet ? 'sheet' : 'dialog'">
                <div
                    v-if="isOpen"
                    ref="surfaceEl"
                    :class="['base-context-menu-surface', isSheet ? 'base-context-menu-sheet' : 'base-context-menu-popover', `base-context-menu-${resolvedAlign}`]"
                    :style="surfaceStyle"
                >
                    <div
                        v-if="isSheet"
                        ref="scrim"
                        class="base-context-menu-scrim"
                        @click="close()"
                    />
                    <div
                        ref="panel"
                        class="menu-panel"
                        @touchstart.passive="onPanelTouchStart"
                        @touchend="onPanelTouchEnd"
                        @touchcancel="onPanelTouchCancel"
                    >
                        <div
                            v-if="isSheet"
                            class="menu-grabber"
                            aria-hidden="true"
                        />
                        <div
                            v-if="header"
                            class="menu-header"
                        >
                            <!-- Decorative: the title sits beside it (and BaseResourceImage's alt
                                 would read "undefined art" for an avatar-only resource). The frame
                                 owns the size (44, or 36 for the avatar circle), radius and
                                 placeholder fill and clips whatever fills it — the cover, or a
                                 consumer's `header-art` slot. -->
                            <div
                                v-if="header.image || $slots['header-art']"
                                :class="['menu-header-art', { 'menu-header-art-round': header.imageType === 'avatar' }]"
                                aria-hidden="true"
                            >
                                <slot name="header-art">
                                    <BaseResourceImage
                                        class="menu-header-image"
                                        :resource="header.image"
                                        :type="header.imageType || 'album_art'"
                                        :fill="false"
                                        :centered="true"
                                        :should-lazyload="false"
                                    />
                                </slot>
                            </div>
                            <div class="menu-header-text">
                                <div class="menu-header-title">{{ header.title }}</div>
                                <div
                                    v-if="header.subtitle"
                                    class="menu-header-subtitle"
                                >{{ header.subtitle }}</div>
                            </div>
                        </div>
                        <!-- role="menu" may own only menuitems, groups and separators, so the
                             grabber and header stay on the panel, outside it. Unstyled on purpose:
                             a plain block wrapper leaves the rows' layout unchanged. -->
                        <div
                            :id="id"
                            class="menu-rows"
                            role="menu"
                            :aria-label="triggerLabel"
                        >
                            <template
                                v-for="(row, index) in rows"
                                :key="row.divider ? `divider-${index}` : row.id"
                            >
                                <div
                                    v-if="row.divider"
                                    class="menu-divider"
                                    role="separator"
                                />
                                <router-link
                                    v-else-if="row.to"
                                    :to="row.to"
                                    :target="row.target"
                                    :class="['menu-row', { 'menu-row-danger': row.danger }]"
                                    role="menuitem"
                                    @click="activate(row)"
                                >
                                    <span class="menu-row-label">{{ row.label }}</span>
                                    <BaseIcon
                                        v-if="row.chevron"
                                        class="menu-row-chevron"
                                        name="chevron-right"
                                        :size="16"
                                    />
                                </router-link>
                                <a
                                    v-else-if="row.href"
                                    :href="row.href"
                                    :class="['menu-row', { 'menu-row-danger': row.danger }]"
                                    role="menuitem"
                                    target="_blank"
                                    rel="noopener"
                                    @click="activate(row)"
                                >
                                    <span class="menu-row-label">{{ row.label }}</span>
                                </a>
                                <button
                                    v-else
                                    type="button"
                                    :class="['menu-row', { 'menu-row-danger': row.danger }]"
                                    role="menuitem"
                                    @click="activate(row)"
                                >
                                    <span class="menu-row-label">{{ row.label }}</span>
                                    <BaseIcon
                                        v-if="row.chevron"
                                        class="menu-row-chevron"
                                        name="chevron-right"
                                        :size="16"
                                    />
                                </button>
                            </template>
                        </div>
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

$popover-width: 280px;
$sheet-width: 375px;
$row-height: 40px;
$row-height-touch: 52px;
$art-size: 44px;
$avatar-size: 36px;

.base-context-menu {
    position: relative;
    display: inline-block;
}

.base-context-menu-surface {
    cursor: default;
}

.base-context-menu-popover {
    position: absolute;
    top: calc(100% + #{$space-4});
    z-index: $layer-message-z-index;
    width: $popover-width;

    &.base-context-menu-end {
        right: 0;
    }

    &.base-context-menu-start {
        left: 0;
    }
}

.base-context-menu-sheet {
    position: fixed;
    inset: 0;
    z-index: $layer-modal-z-index;
    display: flex;
    align-items: flex-end;
    justify-content: center;

    .menu-panel {
        width: 100%;
        max-width: $sheet-width;
        // Figma Account Sheet 531:1182: the home-indicator area IS the bottom padding (34 on a
        // notched iPhone), 32 where there is no inset — never both.
        padding-bottom: max(#{$space-32}, env(safe-area-inset-bottom));
        border-radius: $radius-16 $radius-16 0 0;

        @include elevationDialog;
    }
}

.base-context-menu-scrim {
    position: absolute;
    inset: 0;
    background: $c-surface-scrim;
}

.menu-panel {
    position: relative;
    padding: $space-8;
    text-align: left;
    border-radius: $radius-12;

    @include elevationOverlay;
}

.menu-grabber {
    width: 36px;
    height: 4px;
    margin: 0 auto $space-8;
    background: $c-border-strong;
    border-radius: $radius-full;
}

.menu-header {
    display: flex;
    gap: $space-12;
    align-items: center;
    // Figma .Overlay/Menu Header 421:4: 6 top, 8 sides and bottom. There is no 6 step on the
    // FDS space scale (4·8·12·…), so the top is a literal.
    padding: 6px $space-8 $space-8;
}

.menu-header-art {
    // Flex, not block: the cover or slotted avatar never sits on a line box (which an
    // inherited line-height could push down and clip).
    display: flex;
    flex-shrink: 0;
    overflow: hidden;
    background: $c-surface-raised;
    border-radius: $radius-5;

    @include square($art-size);
}

// The account avatar is the 36 Nav/Avatar circle (509:117; Account Sheet 531:1186), drawn by
// the consumer's header-art slot — smaller than the 44 art frame.
.menu-header-art-round {
    border-radius: $radius-full;

    @include square($avatar-size);
}

// BaseResourceImage's root fills the frame on both axes; `centered` (not `fill`, whose
// padding-bottom box would double the height inside a fixed frame) cover-crops the art.
.menu-header-image {
    @include square(100%);
}

.menu-header-text {
    min-width: 0;
}

.menu-header-title {
    @include bodySmallEmphasis;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.menu-header-subtitle {
    @include secondary;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.menu-divider {
    height: 1px;
    margin: $space-8 0;
    background: $c-border-default;
}

.menu-row {
    @include bodySmall;

    display: flex;
    gap: $space-8;
    align-items: center;
    width: 100%;
    height: $row-height;
    padding: 0 $space-12;
    color: $c-text-primary;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: $radius-8;

    @include transition(background-color, fast);
    @include focusRing;

    &:hover,
    &:visited:hover {
        color: $c-text-primary;
        text-decoration: none;
        background: $c-state-hover;
    }

    &:active {
        background: $c-state-pressed;
    }

    &:visited {
        color: $c-text-primary;
    }

    @media (pointer: coarse) {
        height: $row-height-touch;
    }
}

.menu-row-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.menu-row-chevron {
    flex-shrink: 0;
    color: $c-text-tertiary;
}

.menu-row-danger {
    color: $c-status-error-text;

    &:hover,
    &:visited,
    &:visited:hover {
        color: $c-status-error-text;
    }
}

// Sheet motion (Dialogs §mobile): slide up at base/enter, fade out at fast/exit.
.sheet-enter-active {
    @include transition(opacity, base, enter);

    .menu-panel {
        @include transition(transform, base, enter);
    }
}

.sheet-leave-active {
    @include transition(opacity, fast, exit);
}

.sheet-enter-from {
    opacity: 0;

    .menu-panel {
        transform: translateY(100%);
    }
}

.sheet-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .sheet-enter-from .menu-panel {
        transform: none;
    }
}
</style>
